import { Link, useLocation } from "react-router-dom";
import { Linkedin, ArrowUpRight } from "lucide-react";
import { CONTACT, COMPANY } from "@/data/content";

const PAGES = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Infrastructure", to: "/capabilities/infrastructure" },
  { label: "Products", to: "/capabilities/products" },
  { label: "Industries & Work", to: "/industries" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export default function Footer() {
  const { pathname } = useLocation();
  const showCta = pathname !== "/contact";

  return (
    <footer data-testid="site-footer" className="bg-[#0A0A0A] text-white">
      {/* Big CTA — hidden on Contact page (redundant with the form) */}
      {showCta && (
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 pt-24 md:pt-32 pb-12 md:pb-16">
          <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-white/40 mb-6">
            [ Request a quote ]
          </p>
          <Link
            to="/contact"
            data-testid="footer-cta-link"
            className="group inline-flex items-start gap-4 md:gap-8"
          >
            <h2 className="font-display font-medium tracking-tight text-5xl sm:text-7xl lg:text-8xl leading-[0.9] transition-colors group-hover:text-white/70">
              Let&apos;s Talk
            </h2>
            <ArrowUpRight className="h-10 w-10 md:h-16 md:w-16 mt-2 text-[hsl(var(--signal))] transition-transform duration-500 group-hover:translate-x-2 group-hover:-translate-y-2" />
          </Link>
        </div>
      )}

      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 border-t border-white/10 py-12">
          {/* Brand + contact */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5 mb-6">
              <img src="/assets/brand/logo.png" alt="Krystal Technology" className="h-12 w-auto" />
              <span className="font-display font-extrabold tracking-tight text-lg uppercase">
                Krystal Technology
              </span>
            </div>
            <p className="text-white/50 text-sm max-w-sm leading-relaxed mb-6">
              Manufacturing critical components since 1993
            </p>
            <div className="flex items-center gap-5 mb-8 flex-wrap" data-testid="footer-certifications">
              {[
                { src: "/assets/brand/iso-9001-2015.png", alt: "ISO 9001:2015 Certified" },
                { src: "/assets/brand/iso-14001-2015.png", alt: "ISO 14001:2015 Certified" },
                { src: "/assets/brand/iso-45001-2018.png", alt: "ISO 45001:2018 Certified" },
              ].map((c) => (
                <img key={c.alt} src={c.src} alt={c.alt} className="h-[90px] w-[90px] object-contain" />
              ))}
            </div>
          </div>

          {/* Pages */}
          <div className="md:col-span-3">
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/40 mb-5">Navigate</p>
            <ul className="space-y-3">
              {PAGES.map((p) => (
                <li key={p.to}>
                  <Link
                    to={p.to}
                    data-testid={`footer-link-${p.label.toLowerCase().split(" ")[0]}`}
                    className="text-sm text-white/60 hover:text-white transition-colors link-underline"
                  >
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Office */}
          <div className="md:col-span-4">
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/40 mb-5">Office</p>
            <p className="text-sm text-white/80 font-medium">{CONTACT.office.label}</p>
            <p className="text-sm text-white/45 mt-1 max-w-xs">{CONTACT.office.line}</p>
            <a
              href={CONTACT.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin"
              className="inline-flex items-center gap-2 mt-6 text-sm text-white/70 hover:text-white transition-colors"
            >
              <span className="grid place-items-center h-9 w-9 border border-white/15 hover:border-white/50 transition-colors">
                <Linkedin className="h-4 w-4" />
              </span>
              LinkedIn
            </a>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-white/10 py-8">
          <p className="font-mono-tech text-[11px] uppercase tracking-wider text-white/40">
            © {new Date().getFullYear()} Krystal Technology — {COMPANY.tagline}
          </p>
        </div>
      </div>
    </footer>
  );
}
